import type { QueryClient } from '@tanstack/react-query'
import { fileKeys } from '../files/useFiles'
import type { ProjectFlat } from '../projects/useProjects'

export interface ActivityEvent {
  id: number
  workspace_id: string
  actor_id: string | null
  record_id: string | null
  object_type: string | null
  action: string
  payload: Record<string, unknown> | null
  created_at: string
}

/**
 * Invalidates React Query caches when an SSE event arrives.
 * Query keys are matched to what each hook uses in chrona-app.
 */
export function applyEventToCache(
  qc: QueryClient,
  slug: string,
  event: ActivityEvent,
) {
  const recordType = event.object_type
  if (!recordType) return

  switch (event.action) {
    case 'record.created':
    case 'record.updated':
    case 'record.archived': {
      qc.invalidateQueries({ queryKey: ['task-activity', slug] })
      qc.invalidateQueries({ queryKey: ['project-activity', slug] })
      // Tasks
      if (recordType === 'task' || recordType === 'task_status_segment') {
        qc.invalidateQueries({ queryKey: ['tasks-search', slug] })
        qc.invalidateQueries({ queryKey: ['task-detail-list', slug] })
      }
      // If a segment changed, also invalidate the parent task
      if (recordType === 'task_status_segment' && event.payload?.task_id) {
        qc.invalidateQueries({ queryKey: ['tasks-search', slug] })
        qc.invalidateQueries({ queryKey: ['task-detail-list', slug] })
      }
      // Projects
      if (recordType === 'project') {
        // For updates: apply changes directly into detail cache for instant (zero-lag) update
        if (event.action === 'record.updated' && event.record_id) {
          const changes = event.payload?.changes as
            | Record<string, { from: unknown; to: unknown }>
            | undefined
          if (changes) {
            qc.setQueryData<ProjectFlat>(
              ['project', slug, event.record_id],
              (old) => {
                if (!old) return old
                const patch: Record<string, unknown> = {}
                for (const [field, change] of Object.entries(changes)) {
                  patch[field] = change.to
                }
                return { ...old, ...patch }
              },
            )
            // Mirror into list cache
            qc.setQueryData<ProjectFlat[]>(['projects', slug], (list) => {
              if (!list) return list
              return list.map((p) => {
                if (p.id !== event.record_id) return p
                const patch: Record<string, unknown> = {}
                for (const [field, change] of Object.entries(changes)) {
                  patch[field] = change.to
                }
                return { ...p, ...patch }
              })
            })
          }
        } else {
          // Created / archived: full invalidate
          qc.invalidateQueries({ queryKey: ['projects', slug] })
          if (event.record_id) {
            qc.invalidateQueries({ queryKey: ['project', slug, event.record_id] })
          }
        }
      }
      // People / members
      if (recordType === 'person') {
        qc.invalidateQueries({ queryKey: ['people', slug] })
      }
      // Files / docs
      if (recordType === 'file') {
        qc.invalidateQueries({ queryKey: fileKeys.all(slug) })
      }
      // Comments
      if (recordType === 'comment') {
        const parentId = event.payload?.parent_record_id as string | undefined
        const parentType = event.payload?.parent_record_type as string | undefined
        if (parentId && parentType) {
          qc.invalidateQueries({ queryKey: ['comments', slug, parentType, parentId] })
          // Also refresh the parent task/project detail
          qc.invalidateQueries({ queryKey: ['tasks-search', slug] })
        }
      }
      // Status schemes
      if (recordType === 'status_scheme') {
        qc.invalidateQueries({ queryKey: ['status-scheme', slug] })
      }
      break
    }
    case 'edge.created':
    case 'edge.deleted': {
      // Edges can connect tasks to projects — refresh both
      qc.invalidateQueries({ queryKey: ['tasks-search', slug] })
      qc.invalidateQueries({ queryKey: ['projects', slug] })
      break
    }
    default:
      break
  }
}
